import * as React from "react";
import { useMemo, useState } from "react";
import { Dispatch, connect } from "react-redux";
import { bindActionCreators } from "redux";
import { IStore } from "reducers";
import { List, Map, Set } from "immutable";
import { DirectionalHint, IconButton, mergeStyleSets } from "@nuorder/components";
import { targetsListStyles } from "./targets-list";
import ChevronIconRight from "material-ui/svg-icons/navigation/chevron-right";
import ChevronIconDown from "material-ui/svg-icons/navigation/expand-more";

import Checkbox from "material-ui/Checkbox";
import { formatMessage } from "lib/globalize-util";
import { DATATEST } from "constants/integration";
import { SingleLineLookupFields } from "helpers/merchant/lookup/single-line-lookup-fields";
import { ISchemaKey, parseAssortmentId } from "helpers/product-collection";
import { withRouter } from "react-router";
import { CollectionKeys, TARGET_BAG_ID, ValueTypes } from "constants/product-collection";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { ToggleMetric } from "actions/tool-panel/draft/types";
import { toggleMetric as toggleMetricAction } from "actions/tool-panel/draft/actions";
import { GridApi } from "ag-grid-community";
import { AllocationBag } from "helpers/allocation-dimension";
import { useTargetGroupTotal } from "components/product-collection/product-collection-views/product-collection-target-metrics/hooks";
import { TargetGroupWithTargets } from "components/product-collection/footer/types";
import { IValueTypeMap } from "helpers/merchant/lookup/types";
import {
    RemoveTargetPayload,
    TargetsQueueType,
    removeTargetAction,
    updateQueueTypeAction,
} from "actions/collections/collection-target";
import { useTargetWizardContext } from "../../context/target-wizard-context";
import { DeleteTargetDialog } from "../delete-target-dialog/delete-target-dialog";
import { metricsNumberFormatter } from "helpers/metrics/formatters/metrics-number-formatter";
import { MetricsKind } from "helpers/metrics/types";
import { getTargetGroupsWithTargets } from "reducers/selectors/collection";

interface DependencyValuesAndLatestTimestamp {
    lookUpFieldMap: {
        [targetId: string]: {
            schemaList: ISchemaKey[];
            dependenciesValues: IValueTypeMap;
        };
    };
    earliestCreatedAt: Date;
    latestModifiedAt: Date;
    targetGroupType: string;
}

export const reduceDependencyValuesAndTimestamp = (
    itemDisplayPropertyKeys: List<Map<keyof ISchemaKey, any>>,
    targetGroupWithTargets: TargetGroupWithTargets
): DependencyValuesAndLatestTimestamp => {
    const lookUpFieldMap = {};
    let earliestCreatedAt = undefined;
    let latestTargetModifiedAt = 0;
    let targetGroupType = "";

    targetGroupWithTargets.targets.forEach((target) => {
        const dependenciesValues: IValueTypeMap = {};
        const schemaList: ISchemaKey[] = [];
        const createdAt = target.created_at_ms;
        const modifiedAt = target.modified_at_ms;

        if (!earliestCreatedAt || createdAt < earliestCreatedAt) {
            earliestCreatedAt = createdAt;
        }

        if (modifiedAt > latestTargetModifiedAt) {
            latestTargetModifiedAt = modifiedAt;
        }

        const filters = target.filters;
        filters?.forEach((filter) => {
            const fieldName = filter.field_name;
            const value = filter.value;

            itemDisplayPropertyKeys.forEach((key) => {
                const keyFieldName = key.get("field_name");

                if (keyFieldName === fieldName) {
                    const keyDisplayName = key.get("display_name");
                    const displayNameOverride = key.get("display_name_override");
                    // TODO: ideally we should only assign a value to targetGroupType once
                    targetGroupType = displayNameOverride ? displayNameOverride : keyDisplayName;
                    schemaList.push(key.toJS());
                    dependenciesValues[fieldName] = {
                        value,
                        type: key.get("value_type") as ValueTypes,
                    };
                }
            });
        });

        const targetId = target.id;
        lookUpFieldMap[targetId] = {
            schemaList,
            dependenciesValues,
        };
    });

    return {
        lookUpFieldMap,
        earliestCreatedAt: new Date(earliestCreatedAt),
        latestModifiedAt: new Date(latestTargetModifiedAt),
        targetGroupType,
    };
};

export const calculateMetricIdx = (metricKind: MetricsKind, dimensionsPanelDraft: DimensionsPanelReduxState) => {
    return dimensionsPanelDraft.metrics.values.findIndex((metric) => metric.kind === metricKind);
};

export const isMetricSelectedFromIdx = (idx: number, dimensionsPanelDraft: DimensionsPanelReduxState) => {
    const metricValue = dimensionsPanelDraft.metrics.values[idx];
    return metricValue.showValue || metricValue.showSubtotals || metricValue.showTotals;
};

export const isOneMetricSelected = (dimensionsPanelDraft: DimensionsPanelReduxState) => {
    const metrics = dimensionsPanelDraft.metrics.values.filter(
        (metricValue) => metricValue.showValue || metricValue.showSubtotals || metricValue.showTotals
    );
    return metrics.length === 1;
};

interface OwnProps {
    targetGroupId: string;
}

interface ReduxProps {
    merchantId: string;
    itemDisplayPropertyKeys: List<Map<keyof ISchemaKey, any>>;
    dimensionsPanelApplied: DimensionsPanelReduxState;
    dimensionsPanelDraft: DimensionsPanelReduxState;
    mainGridApi: GridApi;
    targetBag: AllocationBag;
    targetGroupsWithTargets: TargetGroupWithTargets[];
}

interface ReduxDispatchProps {
    removeTarget: (payload: RemoveTargetPayload) => void;
    updateQueueType: (payload: TargetsQueueType) => void;
    toggleMetric: ToggleMetric;
}

const targetGroupStyles = mergeStyleSets({
    targetGroupContainer: {
        borderTop: "1px solid rgb(225, 225, 225)",
    },
    iconContainer: {
        display: "flex",
        flexDirection: "row",
    },
    targetGroupNameContainer: {
        display: "flex",
        flexDirection: "column",
    },
    targetGroupNameText: {
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    targetDescription: {
        fontSize: "1rem",
        color: "grey",
    },
    typeContainer: {
        display: "flex",
        flexDirection: "column",
    },
    typeDescription: {
        fontSize: "1rem",
        color: "grey",
    },
    targetContainer: {
        display: "flex",
        flexDirection: "column",
    },
    targetTextContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    },
    targetTextValue: {
        fontSize: "1.5rem",
    },
    targetCheckboxContainer: {
        width: "35px",
        marginRight: "8px",
    },
    targetTextTargetValue: {
        fontSize: "1rem",
        color: "grey",
    },
    iconButton: {
        border: "none",
        padding: 0,
        background: "none",
        display: "flex",
        justifyContent: "center",
    },
    targetListContainer: {
        display: "flex",
        flexDirection: "column",
        maxHeight: "none",
    },
    targetNameContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    },
    targetListContainerClosed: {
        display: "none",
        maxHeight: 0,
    },
});

export const TargetGroupComponent: React.FunctionComponent<OwnProps & ReduxProps & ReduxDispatchProps> = ({
    targetGroupId,
    targetGroupsWithTargets,
    merchantId,
    itemDisplayPropertyKeys,
    targetBag,
    dimensionsPanelApplied,
    mainGridApi,
    removeTarget,
    updateQueueType,
    toggleMetric,
    dimensionsPanelDraft,
}) => {
    const {
        selectedTargetIds: [selectedTargetIds, setSelectedTargetIds],
    } = useTargetWizardContext();

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

    const [budgetListOpen, setBudgetListOpen] = useState<boolean>(false);
    const {
        actions: { openEditWizard },
    } = useTargetWizardContext();

    const targetGroupWithTargets = useMemo(() => {
        return targetGroupsWithTargets.find((targetGroup) => targetGroup.id === targetGroupId);
    }, [targetGroupId, targetGroupsWithTargets]);

    const { earliestCreatedAt, latestModifiedAt, lookUpFieldMap, targetGroupType } = useMemo(
        () => reduceDependencyValuesAndTimestamp(itemDisplayPropertyKeys, targetGroupWithTargets),
        [itemDisplayPropertyKeys, targetGroupWithTargets]
    );

    const metricDisplayNameMap = useMemo(() => {
        return dimensionsPanelApplied.metrics.values.reduce((acc, metric) => {
            acc[metric.kind] = metric.displayName;
            return acc;
        }, {});
    }, [dimensionsPanelApplied.metrics.values]);

    const { totalBreakdown, total, planTotalBreakdown, planTotal } = useTargetGroupTotal(
        targetBag,
        targetGroupWithTargets,
        targetGroupWithTargets.targets,
        mainGridApi
    );

    const isGroupSelected = React.useMemo(() => {
        if (targetGroupWithTargets.targets.length === 0) {
            return false;
        }

        if (selectedTargetIds.size < 1) {
            return false;
        }

        for (const target of targetGroupWithTargets.targets) {
            if (!selectedTargetIds.has(target.id)) {
                return false;
            }
        }
        return true;
    }, [targetGroupWithTargets, selectedTargetIds]);

    const selectMetricCallback = React.useCallback(
        (select, newSelectedTargetIds: Set<string>) => {
            const metricIdx = calculateMetricIdx(targetGroupWithTargets.metric, dimensionsPanelDraft);
            if (metricIdx >= 0) {
                const isMetricSelected = isMetricSelectedFromIdx(metricIdx, dimensionsPanelDraft);
                if (select !== isMetricSelected) {
                    let triggerToggleMetric = true;
                    // Add check if we're unselecting this metric there aren't other target metrics with same metric selected
                    // or there is only one metric selected
                    if (!select) {
                        const otherTargetMetrics = targetGroupsWithTargets.filter(
                            (targetGroup) =>
                                targetGroup.metric === targetGroupWithTargets.metric &&
                                targetGroup.targets.find((target) => newSelectedTargetIds.has(target.id))
                        );

                        if (otherTargetMetrics.length > 0 || isOneMetricSelected(dimensionsPanelDraft)) {
                            triggerToggleMetric = false;
                        }
                    }

                    if (triggerToggleMetric) {
                        toggleMetric({ index: metricIdx });
                    }
                }
            }
        },
        [dimensionsPanelDraft, toggleMetric, targetGroupWithTargets.metric, targetGroupsWithTargets]
    );

    const checkGroupCallback = React.useCallback(
        (checked) => {
            let newSet = Set<string>(selectedTargetIds);
            if (!checked) {
                targetGroupWithTargets.targets.forEach((target) => {
                    const targetId = target.id;
                    newSet = newSet.delete(targetId);
                });
                setSelectedTargetIds(newSet);
            } else {
                for (const target of targetGroupWithTargets.targets) {
                    const targetId = target.id;
                    newSet = newSet.add(targetId);
                }
                setSelectedTargetIds(newSet);
            }
            selectMetricCallback(checked, newSet);
        },
        [selectedTargetIds, selectMetricCallback, targetGroupWithTargets.targets, setSelectedTargetIds]
    );

    const handleTargetDelete = () => {
        const payload: RemoveTargetPayload = {
            targetGroupId: targetGroupWithTargets.id,
            targetsIds: targetGroupWithTargets.targets.map((target) => target.id),
            queueOptions: {
                targetName: targetGroupWithTargets.name,
            },
        };

        updateQueueType(TargetsQueueType.REMOVE);
        removeTarget(payload);
        setIsDeleteDialogOpen(false);
    };

    const openDeleteDialog = () => {
        setIsDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
    };
    const getNumberFormatForKind = (kind, metricsData) => {
        const metric = metricsData?.values?.find((item) => item?.kind === kind);
        if (metric) {
            return metric.numberFormat;
        }
        return null;
    };
    const formatMetricValue = (value, kind, numberFormat) => {
        const formattedValue = metricsNumberFormatter({
            kind: kind,
            numberFormat: numberFormat,
        })(value);

        return formattedValue;
    };

    // created a state to set the formatted total as per number format object
    const [formattedTotal, setFormattedValue] = React.useState("");
    const [metricDetails, setMetricDetails] = React.useState({ kind: "", numberFormat: {} });

    React.useEffect(() => {
        if (!!dimensionsPanelApplied.metrics && !!targetGroupWithTargets) {
            const kind = targetGroupWithTargets.metric;
            const numberFormat = getNumberFormatForKind(kind, dimensionsPanelApplied.metrics);
            const formattedTotal = formatMetricValue(planTotal, kind, numberFormat);
            setMetricDetails({ kind, numberFormat });
            setFormattedValue(formattedTotal);
        }
    }, [dimensionsPanelApplied, targetGroupWithTargets, planTotal]);

    return (
        <>
            <div
                data-test={DATATEST.ASSORTMENTS.FOOTER.MANAGE_TARGETS.TARGET_GROUP.GROUP_CONTAINER}
                className={`${targetsListStyles.targetsListRowContainer} ${targetGroupStyles.targetGroupContainer}`}
            >
                <div className={targetsListStyles.targetsListRowTinyColumn}>
                    <div className={targetGroupStyles.iconContainer}>
                        {(targetGroupWithTargets?.targets?.length ?? 0) > 0 && (
                            <button
                                data-test={DATATEST.ASSORTMENTS.FOOTER.MANAGE_TARGETS.TARGET_GROUP.EXPAND_BUTTON}
                                role="button"
                                className={targetGroupStyles.iconButton}
                                onClick={() => {
                                    setBudgetListOpen(!budgetListOpen);
                                }}
                            >
                                {budgetListOpen ? (
                                    <ChevronIconDown style={{ marginRight: "8px" }} />
                                ) : (
                                    <ChevronIconRight style={{ marginRight: "8px" }} />
                                )}
                            </button>
                        )}
                    </div>
                </div>
                <div className={targetsListStyles.targetsListRowSmallColumn}>
                    <div className={targetGroupStyles.iconContainer}>
                        <Checkbox
                            data-test={`${DATATEST.ASSORTMENTS.FOOTER.MANAGE_TARGETS.TARGET_GROUP.GROUP_CHECK}-${targetGroupWithTargets.id}`}
                            checked={isGroupSelected}
                            onCheck={(el, checked) => {
                                checkGroupCallback(checked);
                            }}
                        />
                    </div>
                </div>
                <div className={targetsListStyles.targetsListRowLargeColumn}>
                    <div className={targetGroupStyles.targetGroupNameContainer}>
                        <span className={targetGroupStyles.targetGroupNameText}>{targetGroupWithTargets.name}</span>
                        <span className={targetGroupStyles.targetDescription}>{`${formatMessage("Targets")} (${
                            targetGroupWithTargets.targets.length
                        })`}</span>
                    </div>
                </div>
                <div className={targetsListStyles.targetsListRowMediumColumn}>
                    <div className={targetGroupStyles.typeContainer}>
                        <span>{targetGroupType}</span>
                        <span className={targetGroupStyles.typeDescription}>
                            {metricDisplayNameMap[targetGroupWithTargets.metric]}
                        </span>
                    </div>
                </div>
                <div className={targetsListStyles.targetsListRowMediumColumn}>
                    <div className={targetGroupStyles.targetContainer}>
                        <div className={targetGroupStyles.targetTextContainer}>
                            {/* TODO: TARGET This computation is disabled until GA */}
                            {/* <span className={targetGroupStyles.targetTextValue}>{formatValueToCurrency(total)}&nbsp;/&nbsp;</span> */}
                            {/* TODO: TARGET number format */}
                            {/* <span className={targetGroupStyles.targetTextTargetValue}> */}
                            <span>{formattedTotal}</span>
                        </div>
                    </div>
                </div>
                <div className={targetsListStyles.targetsListRowSmallColumn}>
                    <div className={targetGroupStyles.typeContainer}>
                        <span>{earliestCreatedAt.toLocaleDateString()}</span>
                        <span className={targetGroupStyles.typeDescription}>
                            {earliestCreatedAt.toLocaleTimeString()}
                        </span>
                    </div>
                </div>
                <div className={targetsListStyles.targetsListRowSmallColumn}>
                    <div className={targetGroupStyles.typeContainer}>
                        <span>{latestModifiedAt.toLocaleDateString()}</span>
                        <span className={targetGroupStyles.typeDescription}>
                            {latestModifiedAt.toLocaleTimeString()}
                        </span>
                    </div>
                </div>
                <div className={targetsListStyles.targetsListRowTinyColumn}>
                    <div className={targetGroupStyles.iconContainer}>
                        <IconButton
                            role="button"
                            className="item"
                            disabled={false}
                            aria-haspopup
                            menuIconProps={{
                                iconName: "horizKabob24",
                                style: {
                                    display: "flex",
                                    alignItems: "center",
                                },
                            }}
                            menuProps={{
                                directionalHint: DirectionalHint.leftCenter,
                                gapSpace: 0,
                                beakWidth: 20,
                                directionalHintFixed: false,
                                items: [
                                    {
                                        key: "edit",
                                        text: formatMessage("Edit"),
                                        "data-test": DATATEST.ASSORTMENTS.FOOTER.MANAGE_TARGETS.BUDGET_GROUP_EDIT_ITEM,
                                        iconProps: {
                                            iconName: "edit",
                                        },
                                        onClick: () => {
                                            updateQueueType(TargetsQueueType.EDIT);
                                            openEditWizard(targetGroupWithTargets, targetBag);
                                        },
                                    },
                                    {
                                        key: "delete",
                                        text: formatMessage("Delete"),
                                        "data-test":
                                            DATATEST.ASSORTMENTS.FOOTER.MANAGE_TARGETS.BUDGET_GROUP_DELETE_ITEM,
                                        iconProps: {
                                            iconName: "delete",
                                        },
                                        onClick: openDeleteDialog,
                                    },
                                ],
                            }}
                        />
                    </div>
                </div>
            </div>
            <div
                data-test={DATATEST.ASSORTMENTS.FOOTER.MANAGE_TARGETS.TARGET_GROUP.GROUP_SUBLIST_CONTAINER}
                className={`${targetGroupStyles.targetListContainer} ${
                    !budgetListOpen && targetGroupStyles.targetListContainerClosed
                }`}
            >
                {targetGroupWithTargets.targets.map((target) => {
                    const targetId = target.id;
                    return (
                        <div
                            data-test={`${DATATEST.ASSORTMENTS.FOOTER.MANAGE_TARGETS.TARGET_GROUP.SUB_LIST.CONTAINER}-${targetId}`}
                            key={targetId}
                            className={`${targetsListStyles.targetsListRowContainer} ${targetGroupStyles.targetGroupContainer}`}
                        >
                            <div className={targetsListStyles.targetsListRowTinyColumn} />
                            <div className={targetsListStyles.targetsListRowSmallColumn} />
                            <div className={targetsListStyles.targetsListRowMediumLargeColumn}>
                                <div className={targetGroupStyles.targetNameContainer}>
                                    <div className={targetGroupStyles.targetCheckboxContainer}>
                                        <Checkbox
                                            checked={selectedTargetIds.has(targetId)}
                                            onCheck={(_el, checked) => {
                                                let newSet = Set(selectedTargetIds);
                                                if (checked) {
                                                    newSet = newSet.add(targetId);
                                                } else {
                                                    newSet = newSet.delete(targetId);
                                                }
                                                setSelectedTargetIds(newSet);
                                                selectMetricCallback(checked, newSet);
                                            }}
                                        />
                                    </div>
                                    {/* Target type assortment doesn't have filters */}
                                    {target.filters?.length === 0 ? (
                                        <div>{formatMessage("Assortment (plain text)")}</div>
                                    ) : (
                                        <SingleLineLookupFields
                                            merchantId={merchantId}
                                            schemaList={lookUpFieldMap[targetId]?.schemaList}
                                            dependenciesValues={lookUpFieldMap[targetId]?.dependenciesValues}
                                            fieldRenderer={(text, index) => (
                                                <>
                                                    {text +
                                                        (index !==
                                                        (lookUpFieldMap[targetId]?.schemaList?.length - 1 ?? 0)
                                                            ? " | "
                                                            : "")}
                                                </>
                                            )}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className={targetsListStyles.targetsListRowLargeColumn}>
                                <div className={targetGroupStyles.targetContainer}>
                                    <div className={targetGroupStyles.targetTextContainer}>
                                        {/* TODO: TARGET This computation is disabled until GA */}
                                        {/* <span className={targetGroupStyles.targetTextValue}>
                                            {formatValueToCurrency(totalBreakdown[targetId])}&nbsp;/&nbsp;
                                        </span> */}
                                        {/* <span className={targetGroupStyles.targetTextTargetValue}> */}
                                        <span>
                                            {formatMetricValue(
                                                planTotalBreakdown[targetId],
                                                metricDetails.kind,
                                                metricDetails.numberFormat
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className={targetsListStyles.targetsListRowSmallColumn} />
                            <div className={targetsListStyles.targetsListRowSmallColumn} />
                            <div className={targetsListStyles.targetsListRowTinyColumn} />
                        </div>
                    );
                })}
            </div>
            {isDeleteDialogOpen && (
                <DeleteTargetDialog
                    isDialogOpen={isDeleteDialogOpen}
                    closeDialog={closeDeleteDialog}
                    targetName={targetGroupWithTargets.name}
                    onConfirm={handleTargetDelete}
                />
            )}
        </>
    );
};

const mapStateToProps = (state: IStore, ownProps) => {
    const targetBag = state.mutate.snapshots && state.mutate.snapshots.get(TARGET_BAG_ID);

    const { validAssortmentIds } = state.collection || {};

    const { isRollupMode, baseAssortmentId: _baseAssortmentId } = parseAssortmentId(ownProps.params?.id);
    const baseAssortmentId =
        isRollupMode && validAssortmentIds?.length && validAssortmentIds[0] !== _baseAssortmentId
            ? validAssortmentIds[0]
            : _baseAssortmentId;

    const collection = state.mutate.snapshots && state.mutate.snapshots.get(baseAssortmentId);
    const merchantId = collection?.getIn(["merchant_v1_id", "string_value"], "");
    const itemDisplayPropertyKeys = collection?.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS);

    return {
        merchantId,
        itemDisplayPropertyKeys,
        targetBag,
        dimensionsPanelApplied: state.dimensionsPanel.applied,
        dimensionsPanelDraft: state.dimensionsPanel.draft,
        mainGridApi: state.collection.gridApi,
        targetGroupsWithTargets: getTargetGroupsWithTargets(state),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<IStore>) => {
    const removeTarget = (payload: RemoveTargetPayload) => dispatch(removeTargetAction(payload));
    const updateQueueType = (payload: TargetsQueueType) => dispatch(updateQueueTypeAction(payload));
    const toggleMetric = bindActionCreators(toggleMetricAction, dispatch);
    return { removeTarget, updateQueueType, toggleMetric };
};

export const TargetGroup = withRouter(
    connect<ReduxProps, ReduxDispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(TargetGroupComponent)
);
